import interfaceSensorConfig from '@/api/interfaceSensor'
import InputLabelField from '@/components/form-control/InputLabelField'
import Select from '@/components/form-control/Select'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import { usePermission } from '@/hooks/permissions'
import {
  useAddEtZone,
  useEtZoneById,
  useUpdateEtZone,
} from '@/features/config/hooks/etZone'
import { SoilSensor } from '@/types/weather'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import * as yup from 'yup'
import useTranslation from '@/hooks/useTranslation'

interface ETZViewProps {
  id?: string
}

const EtzView = ({ id }: ETZViewProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const { isViewOnly } = usePermission()
  const schema = useMemo(() => {
    return yup.object({
      name: yup
        .string()
        .required(t('formsValidationMicroClimateNameIsRequired')),
      sensorName: yup
        .object()
        .shape({
          id: yup.mixed().required(t('formsValidationSensorIdIsRequired')),
          sensorName: yup
            .string()
            .required(t('formsValidationSensorNameIsRequired')),
        })
        .nullable()
        .required(t('formsValidationMicroClimateSensorIsRequired')),
    })
  }, [t])

  const [soilSensor, setSoilSensor] = useState<SoilSensor[] | undefined>()

  const { mutate: addEtZone, isLoading: loadingAddEtZone } = useAddEtZone()

  const { mutate: editEtZone, isLoading: loadingEditEtZone } = useUpdateEtZone()

  const inputRefSiteName = useRef<HTMLInputElement>(null)
  const { data: dataEditEtZone, isLoading: loadingEtZone } = useEtZoneById(
    interfaceId,
    id,
  )

  const mode = id ? 'edit' : 'create'
  const isLoading = loadingAddEtZone || loadingEditEtZone
  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  const form = useForm({
    defaultValues: {
      name: '',
      sensorName: null,
      ...dataEditEtZone,
    },
    resolver: yupResolver(schema),
  })

  const soilSelected = useWatch({
    control: form.control,
    name: 'sensorName',
  })

  useEffect(() => {
    if (dataEditEtZone?.id) {
      form.reset({
        name: dataEditEtZone.name,
      })
    } else {
      form.reset({
        name: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEditEtZone])

  useEffect(() => {
    if (soilSensor && soilSensor.length > 0) {
      if (dataEditEtZone?.sensorId) {
        const valueWeather = soilSensor.find(
          (i: SoilSensor) => i.id === dataEditEtZone.sensorId,
        )
        if (valueWeather) {
          form.setValue('sensorName', valueWeather as any)
        }
      } else if (mode === 'create') {
        form.setValue('sensorName', (soilSensor[0] ?? null) as any)
      }
    }
  }, [soilSensor, dataEditEtZone, mode, form])

  const handleSave = (data: any) => {
    // ViewOnly users cannot edit or add microclimate
    if (isViewOnly) return

    if (mode === 'create') {
      addEtZone({
        interfaceId: Number(interfaceId || 0),
        name: data.name,
        sensorId: data.sensorName?.id,
      })
    }
    if (mode === 'edit') {
      editEtZone({
        id: Number(id),
        interfaceId: Number(interfaceId || 0),
        name: data.name,
        sensorId: data.sensorName?.id,
      })
    }
  }

  const getSoilSensorET = async (_pageIndex?: number, _pageSize?: number) => {
    try {
      const res = await interfaceSensorConfig.getAllByInterfaceId(
        Number(interfaceId || 0),
      )
      const raw: any[] = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : []
      const soilMappings = raw.filter((a: any) => a.type === 'Soil Sensor')
      const sensors = soilMappings.map((a: any) => {
        const assetId = Number(a.assetId)
        return {
          id: a.id,
          sensorName: a.customName || `Soil Sensor (${assetId})`,
        }
      })
      setSoilSensor(sensors as any)
      return {
        page: 1,
        size: sensors.length,
        totalPage: 1,
        totalCount: sensors.length,
        data: sensors,
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
    }
  }

  const totalLoading = loadingEtZone

  return (
    <form onSubmit={form.handleSubmit(handleSave)}>
      <Container sx={{ maxWidth: '450px', height: '80vh' }}>
        {totalLoading && (
          <Stack
            width="377px"
            height="100%"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Stack>
        )}
        {!totalLoading && (
          <Fade in={!totalLoading}>
            <Stack sx={{ padding: '34px 18px' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '377px' }}
              >
                <Typography
                  sx={{
                    fontWeight: '500',
                    lineHeight: '14px',
                    color: 'text.main',
                  }}
                >
                  {id
                    ? `${t('commonActionsEdit')} ${t('microClimate')}`
                    : `${t('commonActionsAdd')} ${t('microClimate')}`}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="secondary"
                    sx={{ width: '85px', height: '26px' }}
                    onClick={() => closeSidebar()}
                    type="button"
                  >
                    {t('commonActionsCancel')}
                  </Button>
                  <LoadingButton
                    loading={isLoading}
                    size="small"
                    variant="primary"
                    sx={{
                      minWidth: '85px',
                      height: '26px',
                      opacity: isViewOnly ? 0.6 : 1,
                    }}
                    type="submit"
                    disabled={isViewOnly}
                  >
                    {t('commonActionsSave')}
                  </LoadingButton>
                </Stack>
              </Stack>

              <InputLabelField
                name="name"
                form={form}
                label={t('MicroClimateName')}
                placeholder={t('microClimate')}
                inputRef={inputRefSiteName}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  width: { xs: '377px' },
                  marginTop: '22px',
                  opacity: isViewOnly ? 0.6 : 1,
                }}
                padding="8px 16px"
                isRequired
                disabled={isViewOnly}
              />

              {getSoilSensorET && (
                <Select
                  label={t('mapView.content.soilMoistureSensor')}
                  name="sensorName"
                  form={form}
                  bindKey="id"
                  bindLabel="sensorName"
                  getOptions={getSoilSensorET}
                  loadOptionInit={true}
                  isDisabled={isViewOnly}
                  sx={{ opacity: isViewOnly ? 0.6 : 1 }}
                />
              )}
            </Stack>
          </Fade>
        )}
      </Container>
    </form>
  )
}

export default EtzView
